/**
 * Editable content for the Starline Ventures one-pager.
 * Update copy, contact info, and section data here.
 */

export const site = {
  name: 'Starline Ventures',
  established: 2010,
  cities: ['New Orleans', 'Miami', 'Washington, DC', 'Los Angeles'],
};

export const nav = [
  { href: '#overview', label: 'Overview' },
  { href: '#capabilities', label: 'Capabilities' },
  { href: '#approach', label: 'Approach' },
  { href: '#work', label: 'Select Work' },
  { href: '#contact', label: 'Contact' },
] as const;

export const hero = {
  eyebrow: 'Technology · Media · Strategy',
  headline: 'Strategic execution for ambitious organizations.',
  sub: 'Starline Ventures is a boutique advisory and delivery firm. We help operators clarify, build, and launch the digital systems that move the business forward — across product, infrastructure, media, and growth.',
  ctaPrimary: { label: 'Start a conversation', href: '#contact' },
  ctaSecondary: { label: 'See how we work', href: '#capabilities' },
};

export const positioning = [
  'Strategy through execution',
  'Product, media, and digital systems',
  'Senior-led engagements',
  'New Orleans · Washington, DC',
] as const;

export const capabilities = [
  {
    title: 'Digital Strategy',
    icon: 'compass',
    body: 'Operating plans, technology roadmaps, and investment decisions grounded in how the business actually works.',
  },
  {
    title: 'Product & Platform',
    icon: 'layers',
    body: 'Design and delivery of the software that runs your company — from internal tools to customer-facing products.',
  },
  {
    title: 'Media & Audience',
    icon: 'radio',
    body: 'Distribution systems, publishing infrastructure, and content operations for organizations with a point of view.',
  },
  {
    title: 'Infrastructure & Security',
    icon: 'shield',
    body: 'Cloud architecture, hardening, and operational discipline for teams that need to move quickly without breaking the important things.',
  },
  {
    title: 'Research & Prototyping',
    icon: 'flask',
    body: 'Applied research, rapid prototypes, and technical evaluations — useful when the right answer is not yet obvious.',
  },
  {
    title: 'Decision Support',
    icon: 'gauge',
    body: 'Data, reporting, and analytical tooling that turn day-to-day operations into evidence leadership can act on.',
  },
] as const;

export const approach = [
  {
    step: '01',
    title: 'Clarify',
    body: 'Understand the business, the constraints, and the actual problem. Good decisions start with an honest diagnosis.',
  },
  {
    step: '02',
    title: 'Design',
    body: 'Shape the right system — technical, organizational, and commercial — before writing code or signing contracts.',
  },
  {
    step: '03',
    title: 'Execute',
    body: 'Deliver with senior operators who ship. Fewer handoffs, sharper accountability, and systems that hold up in production.',
  },
] as const;

export const workAreas = [
  {
    label: 'Founders & Operators',
    body: 'Early- and growth-stage teams translating traction into durable systems and organizational leverage.',
  },
  {
    label: 'Media & Creative Businesses',
    body: 'Publishers, studios, and independent creators modernizing distribution, monetization, and production workflows.',
  },
  {
    label: 'Service Firms',
    body: 'Professional and advisory firms reworking the software and data practices that sit under their engagements.',
  },
  {
    label: 'Software Ventures',
    body: 'Product-led companies navigating platform decisions, infrastructure shifts, and technical leadership gaps.',
  },
  {
    label: 'Mission-Driven Organizations',
    body: 'Civic, political, and educational operators that need technology to match the seriousness of their work.',
  },
  {
    label: 'Applied AI',
    body: 'Teams integrating modern models into real products — with attention to cost, reliability, and user trust.',
  },
] as const;

export const principles = [
  {
    label: 'Senior-led delivery',
    body: 'The people scoping the work are the people doing the work.',
  },
  {
    label: 'Execution over theater',
    body: 'Fewer slides, more working systems. Momentum compounds.',
  },
  {
    label: 'Clarity as a product',
    body: 'The best decision is usually the one the team can actually carry out.',
  },
] as const;

export const contact = {
  tagline: 'Experienced partners bringing the digital universe under control',
  email: 'dfinley@starlineventures.com',
  phone: '504.298.9354',
  cities: 'New Orleans · Miami · Washington, DC · Los Angeles',
  closer:
    'If you are weighing a significant technology, media, or operational decision, we are happy to think it through with you.',
};
